import React, { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  moduleName?: string
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Error boundary for catching and displaying runtime React errors.
 *
 * Wrap each module route with an ErrorBoundary to prevent a module error
 * from crashing the entire application shell.
 *
 * Usage:
 *   <ErrorBoundary moduleName="Todo">
 *     <TodoModule />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  private isChunkLoadError(error?: Error | null): boolean {
    if (!error) return false
    const msg = error.message || ''
    return (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('error loading dynamically imported module') ||
      msg.includes('Loading chunk') ||
      error.name === 'ChunkLoadError'
    )
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[ErrorBoundary:${this.props.moduleName ?? 'Unknown'}]`, error, info)

    // If dynamic chunk loading failed (e.g. new release deployed), automatically reload once
    if (this.isChunkLoadError(error)) {
      const lastReload = sessionStorage.getItem('nttd_eb_reload')
      const now = Date.now()
      if (!lastReload || now - parseInt(lastReload, 10) > 15000) {
        sessionStorage.setItem('nttd_eb_reload', now.toString())
        console.warn('[ErrorBoundary] Stale chunk detected. Refreshing page...')
        window.location.reload()
      }
    }
  }

  private sanitizeMessage(rawMessage?: string): string {
    if (!rawMessage) return 'An unexpected error occurred.'
    if (this.isChunkLoadError(this.state.error)) {
      return 'A new version of No Time To Delay was deployed. Please refresh the page to load the latest updates.'
    }
    // Strip file paths, JSX call frames, and internal system details for security
    if (
      rawMessage.includes('/Users/') ||
      rawMessage.includes('/home/') ||
      rawMessage.includes('jsxDEV') ||
      rawMessage.includes('fileName') ||
      rawMessage.includes('\\Users\\') ||
      rawMessage.length > 120
    ) {
      return 'An unexpected issue occurred while rendering this module. Please try again.'
    }
    return rawMessage
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const isChunk = this.isChunkLoadError(this.state.error)

      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 24px',
          gap: '16px',
          textAlign: 'center',
        }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: isChunk ? 'rgba(99, 102, 241, 0.15)' : 'var(--color-error-subtle)',
            color: isChunk ? 'var(--color-accent)' : 'inherit',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
          }}>
            {isChunk ? '🔄' : '⚠'}
          </div>
          <div>
            <h3 style={{ color: 'var(--color-text-primary)', marginBottom: 8 }}>
              {isChunk
                ? `${this.props.moduleName ?? 'Module'} Update Available`
                : this.props.moduleName
                  ? `${this.props.moduleName} encountered an error`
                  : 'Something went wrong'}
            </h3>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px', marginBottom: 16 }}>
              {this.sanitizeMessage(this.state.error?.message)}
            </p>
            <button
              onClick={() => {
                if (isChunk) {
                  window.location.reload()
                } else {
                  this.setState({ hasError: false, error: null })
                }
              }}
              style={{
                background: 'var(--color-accent)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 18px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              {isChunk ? 'Refresh & Update' : 'Try again'}
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
